import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';

import {
  InputFormatValidationError,
  useInputFormat,
  useSaveInputFormat,
} from '../../../services/llm-input-format.service';
import { InputFieldDef, InputFormatListItem, LLMProvider } from '../../../types/admin';
import { fieldIssuesFromServer } from './fieldIssues';
import { ImportSchemaDialog } from './ImportSchemaDialog';
import { InputFieldListEditor } from './InputFieldListEditor';
import { rewriteOrder } from './inputFieldOrder';
import { mergeImportedFields } from './mergeImportedFields';

interface InputFormatDialogProps {
  open: boolean;
  /** Existing format to edit; undefined = create. */
  format?: InputFormatListItem | null;
  providers: LLMProvider[];
  onClose: () => void;
}

export const InputFormatDialog: React.FC<InputFormatDialogProps> = ({
  open,
  format,
  providers,
  onClose,
}) => {
  const isEdit = Boolean(format);
  const { data: detail, isLoading } = useInputFormat(open ? format?.publicId : undefined);
  const saveFormat = useSaveInputFormat();
  const [code, setCode] = React.useState('');
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [providerPublicId, setProviderPublicId] = React.useState<string | null>(null);
  const [isActive, setIsActive] = React.useState(true);
  const [fields, setFields] = React.useState<InputFieldDef[]>([]);
  const [tab, setTab] = React.useState<'in' | 'out'>('in');
  const [serverErrors, setServerErrors] = React.useState<string[]>([]);
  const [importOpen, setImportOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setTab('in');
    setServerErrors([]);
    if (!isEdit) {
      setCode('');
      setName('');
      setDescription('');
      setProviderPublicId(null);
      setIsActive(true);
      setFields([]);
    }
  }, [open, isEdit]);

  React.useEffect(() => {
    if (!detail) return;
    setCode(detail.code);
    setName(detail.name);
    setDescription(detail.description ?? '');
    setProviderPublicId(detail.providerPublicId);
    setIsActive(detail.isActive);
    setFields(detail.fields ?? []);
  }, [detail]);

  const inCount = fields.filter((field) => (field.direction ?? 'in') === 'in').length;
  const outCount = fields.length - inCount;
  const selectedProvider = providers.find((provider) => provider.publicId === providerPublicId);

  const save = async () => {
    setServerErrors([]);
    try {
      await saveFormat.mutateAsync({
        publicId: format?.publicId,
        ...(isEdit ? {} : { code: code.trim() }),
        name: name.trim(),
        description: description.trim() || (isEdit ? null : undefined),
        providerPublicId: providerPublicId ?? (isEdit ? null : undefined),
        fields: rewriteOrder(fields),
        isActive,
      });
      onClose();
    } catch (error) {
      if (error instanceof InputFormatValidationError) setServerErrors(error.errors);
    }
  };

  const canSave = name.trim() !== '' && (isEdit || code.trim() !== '') && !saveFormat.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{isEdit ? `Edit input format` : 'New input format'}</DialogTitle>
      <DialogContent>
        {isEdit && isLoading ? (
          <Box className="flex justify-center p-8">
            <CircularProgress />
          </Box>
        ) : (
          <Box className="space-y-4 pt-2">
            <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextField
                required
                label="Name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                helperText='e.g. "Google text to video"'
              />
              <TextField
                required
                label="Code"
                value={code}
                disabled={isEdit}
                onChange={(event) => setCode(event.target.value)}
                helperText={isEdit ? 'Code cannot be changed' : 'Unique, e.g. google-text-to-video'}
              />
              <Autocomplete
                options={providers}
                value={selectedProvider ?? null}
                getOptionLabel={(provider) => provider.name}
                isOptionEqualToValue={(a, b) => a.publicId === b.publicId}
                onChange={(_event, provider) => setProviderPublicId(provider?.publicId ?? null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Provider (optional filter)"
                    helperText="Only narrows the format list in the model dialog"
                  />
                )}
              />
              <TextField
                label="Description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(event) => setIsActive(event.target.checked)}
                />
              }
              label="Active"
            />

            {serverErrors.length > 0 && (
              <Alert severity="error">
                <Box component="ul" className="m-0 pl-4">
                  {fieldIssuesFromServer(serverErrors).map((issue, index) => (
                    <li key={`${issue.field ?? 'format'}-${index}`}>
                      {issue.field ? `${issue.field}: ${issue.message}` : issue.message}
                    </li>
                  ))}
                </Box>
              </Alert>
            )}

            <Box className="flex justify-end">
              <Button
                size="small"
                onClick={() => setImportOpen(true)}
                disabled={saveFormat.isPending}
              >
                Import JSON Schema
              </Button>
            </Box>

            <Tabs value={tab} onChange={(_event, next) => setTab(next)}>
              <Tab value="in" label={`Input fields (${inCount})`} />
              <Tab value="out" label={`Response fields (${outCount})`} />
            </Tabs>
            {tab === 'in' ? (
              <InputFieldListEditor
                direction="in"
                fields={fields}
                onChange={setFields}
                disabled={saveFormat.isPending}
              />
            ) : (
              <>
                <Alert severity="info">
                  Tell the platform where to read the result in the provider response (video URL,
                  status, error, usage). Leave empty to use the built-in parsing.
                </Alert>
                <InputFieldListEditor
                  direction="out"
                  fields={fields}
                  onChange={setFields}
                  disabled={saveFormat.isPending}
                />
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <ImportSchemaDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={(imported) => setFields((current) => mergeImportedFields(current, imported))}
      />
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={save}
          startIcon={saveFormat.isPending ? <CircularProgress size={16} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
